import React from 'react';
import ReactMarkdown from 'react-markdown';
import { extractTableData } from '../utils/markdownTableParser';

/**
 * MarkdownDisplay - A component for rendering markdown text with beautifully styled tables
 *
 * @param {string} content - The markdown content to render
 * @param {string} className - Additional CSS classes to apply to the container
 */
const MarkdownDisplay = ({ content, className = '' }) => {
  if (!content) return null;

  // Find all tables in the content
  const tableRegex = /(\|.+\|\n)+/g;
  const tableMatches = [];
  let match;

  while ((match = tableRegex.exec(content)) !== null) {
    tableMatches.push({
      index: match.index,
      length: match[0].length,
      type: 'table',
      content: match[0]
    });
  }

  // If no tables, render normally with ReactMarkdown
  if (tableMatches.length === 0) {
    return (
      <div className={className}>
        <ReactMarkdown components={markdownComponents}>
          {content}
        </ReactMarkdown>
      </div>
    );
  }

  // Split content into parts (text and tables)
  const parts = [];
  let lastIndex = 0;

  tableMatches.forEach((tableMatch) => {
    // Add text before this table
    if (tableMatch.index > lastIndex) {
      parts.push({
        type: 'text',
        content: content.substring(lastIndex, tableMatch.index)
      });
    }

    // Add the table
    parts.push(tableMatch);

    lastIndex = tableMatch.index + tableMatch.length;
  });

  // Add remaining text
  if (lastIndex < content.length) {
    parts.push({
      type: 'text',
      content: content.substring(lastIndex)
    });
  }

  return (
    <div className={className}>
      {parts.map((part, idx) => {
        if (part.type === 'text') {
          return (
            <ReactMarkdown key={idx} components={markdownComponents}>
              {part.content}
            </ReactMarkdown>
          );
        } else if (part.type === 'table') {
          const tableData = extractTableData(part.content);
          if (!tableData) return null;

          return (
            <div key={idx} className="not-prose overflow-x-auto my-4">
              <table className="min-w-full border-collapse border border-gray-300 rounded-lg shadow-md">
                <thead className="bg-slate-700">
                  <tr>
                    {tableData.header.map((cell, i) => (
                      <th
                        key={i}
                        className="px-6 py-3 text-left text-sm font-semibold text-white border-r border-slate-600 last:border-r-0"
                      >
                        {cell}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tableData.rows.map((row, i) => (
                    <tr key={i} className="even:bg-gray-50 hover:bg-gray-100 transition-colors">
                      {row.map((cell, j) => (
                        <td
                          key={j}
                          className="px-6 py-4 text-sm text-gray-900 border-r border-gray-200 last:border-r-0"
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

// Markdown component customizations
const markdownComponents = {
  // Headings
  // eslint-disable-next-line jsx-a11y/heading-has-content
  h1: ({ node, ...props }) => <h1 className="text-2xl font-bold mb-3 mt-4" {...props} />,
  // eslint-disable-next-line jsx-a11y/heading-has-content
  h2: ({ node, ...props }) => <h2 className="text-xl font-bold mb-2 mt-3" {...props} />,
  // eslint-disable-next-line jsx-a11y/heading-has-content
  h3: ({ node, ...props }) => <h3 className="text-lg font-bold mb-2 mt-3" {...props} />,
  // eslint-disable-next-line jsx-a11y/heading-has-content
  h4: ({ node, ...props }) => <h4 className="text-base font-bold mb-2 mt-2" {...props} />,
  // eslint-disable-next-line jsx-a11y/heading-has-content
  h5: ({ node, ...props }) => <h5 className="text-sm font-bold mb-1 mt-2" {...props} />,
  // eslint-disable-next-line jsx-a11y/heading-has-content
  h6: ({ node, ...props }) => <h6 className="text-sm font-bold mb-1 mt-2" {...props} />,

  // Paragraphs
  p: ({ node, ...props }) => <p className="mb-3 leading-relaxed" {...props} />,

  // Strong/Bold
  strong: ({ node, ...props }) => <strong className="font-bold" {...props} />,

  // Emphasis/Italic
  em: ({ node, ...props }) => <em className="italic" {...props} />,

  // Lists
  ul: ({ node, ...props }) => <ul className="list-disc list-inside mb-3 ml-4" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal list-inside mb-3 ml-4" {...props} />,
  li: ({ node, ...props }) => <li className="mb-1" {...props} />,

  // Code
  code: ({ node, inline, ...props }) =>
    inline
      ? <code className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-sm font-mono" {...props} />
      : <code className="block bg-gray-100 text-gray-800 p-3 rounded text-sm font-mono overflow-x-auto mb-3" {...props} />,

  // Blockquotes
  blockquote: ({ node, ...props }) => (
    <blockquote className="border-l-4 border-gray-300 pl-4 italic my-3 text-gray-700" {...props} />
  ),

  // Links
  a: ({ node, children, ...props }) => (
    // eslint-disable-next-line jsx-a11y/anchor-has-content
    <a className="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
  ),

  // Horizontal rule
  hr: ({ node, ...props }) => <hr className="my-4 border-gray-300" {...props} />,

  // Tables (fallback - primary rendering is done above)
  table: ({ node, ...props }) => (
    <div className="overflow-x-auto mb-3">
      <table className="min-w-full border-collapse border border-gray-300 rounded-lg shadow-md" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => (
    <thead className="bg-slate-700" {...props} />
  ),
  th: ({ node, ...props }) => (
    <th className="px-6 py-3 text-left text-sm font-semibold text-white border-r border-slate-600 last:border-r-0" {...props} />
  ),
  tbody: ({ node, ...props }) => (
    <tbody className="bg-white divide-y divide-gray-200" {...props} />
  ),
  tr: ({ node, ...props }) => (
    <tr className="even:bg-gray-50 hover:bg-gray-100 transition-colors" {...props} />
  ),
  td: ({ node, ...props }) => (
    <td className="px-6 py-4 text-sm text-gray-900 border-r border-gray-200 last:border-r-0" {...props} />
  ),
};

export default MarkdownDisplay;
